import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCaptions } from '../context/CaptionContext';
import WordPopup from './WordPopup';
import LineSettingsPopup from './LineSettingsPopup';
import { useI18n, rich } from '../../i18n';
import './CaptionEditor.css';

// Icons
const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Time Control Component
const TimeControl = ({ time, onChange, isActive, label }) => {
  const [val, setVal] = useState(formatTimeInput(time));
  const timeRef = useRef(time);
  const intervalRef = useRef(null);

  useEffect(() => {
    setVal(formatTimeInput(time));
    timeRef.current = time;
  }, [time]);

  const commit = () => {
    const secs = parseTimeInput(val);
    if (!isNaN(secs)) onChange(secs);
    else setVal(formatTimeInput(time));
  };

  const startAdjusting = (delta) => {
    if (intervalRef.current) return;
    const adjust = () => {
      const newTime = Math.max(0, timeRef.current + delta);
      onChange(newTime);
    };
    adjust();
    intervalRef.current = window.setInterval(adjust, 200);
  };

  const stopAdjusting = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopAdjusting();
  }, []);

  return (
    <div className={`flex items-center justify-between ps-4 pe-1 py-1.5 rounded-xl border transition-all duration-200 group/control w-[130px] ${
      isActive 
        ? 'bg-brand/5 border-brand/20 shadow-sm' 
        : 'bg-slate-50 border-slate-100 hover:border-slate-200 hover:bg-white'
    }`}>
      <div className="flex flex-col w-full me-1">
        {label && (
          <span className={`text-[9px] font-extrabold uppercase tracking-widest mb-0.5 ${
            isActive ? 'text-brand/60' : 'text-slate-400'
          }`}>
            {label}
          </span>
        )}
        <input
          className={`bg-transparent w-full text-[13px] font-mono font-bold outline-none tracking-tight ${
            isActive ? 'text-brand' : 'text-slate-600 group-hover/control:text-slate-800'
          }`}
          value={val}
          dir="ltr"
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
        />
      </div>
      <div className="flex flex-col gap-[2px] border-s border-slate-200/60 ps-1">
        <button
          onMouseDown={() => startAdjusting(0.01)}
          onMouseUp={stopAdjusting}
          onMouseLeave={stopAdjusting}
          onTouchStart={() => startAdjusting(0.01)}
          onTouchEnd={stopAdjusting}
          className={`w-6 h-4 flex items-center justify-center rounded transition-all active:scale-90 select-none ${
            isActive ? 'text-brand hover:bg-brand/10' : 'text-slate-400 hover:bg-slate-200 hover:text-slate-600'
          }`}
        >
          <ChevronUpIcon />
        </button>
        <button
          onMouseDown={() => startAdjusting(-0.01)}
          onMouseUp={stopAdjusting}
          onMouseLeave={stopAdjusting}
          onTouchStart={() => startAdjusting(-0.01)}
          onTouchEnd={stopAdjusting}
          className={`w-6 h-4 flex items-center justify-center rounded transition-all active:scale-90 select-none ${
            isActive ? 'text-brand hover:bg-brand/10' : 'text-slate-400 hover:bg-slate-200 hover:text-slate-600'
          }`}
        >
          <ChevronDownIcon />
        </button>
      </div>
    </div>
  );
};

// Helper functions for time formatting
const formatTimeInput = (seconds) => {
  const date = new Date(0);
  date.setMilliseconds(seconds * 1000);
  return date.toISOString().substring(14, 23);
};

const parseTimeInput = (timeStr) => {
  try {
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      const [m, s] = parts;
      return (parseInt(m) * 60) + parseFloat(s);
    }
    if (parts.length === 3) {
      const [h, m, s] = parts;
      return (parseInt(h) * 3600) + (parseInt(m) * 60) + parseFloat(s);
    }
    return 0;
  } catch (e) {
    return 0;
  }
};

const CaptionEditor = ({ searchMatchIds = new Set(), currentSearchMatchId = null, autoSelectWordId = null, onAutoSelectHandled, projectLanguage = 'ar', isTrialProtected = false, onVerifyClick, onUpgradeClick }) => {
  const { t } = useI18n();
  const {
    captionData,
    viewCaptionData, // Use this for display when respecting translation view
    isTranslationView,
    currentTime,
    getWordById,
    updateSkeleton,
    updateSkeletonTiming,
    deleteSkeleton,
    addSkeleton,
    addWord,
    removeWord,
    seekToTime,
    themeSettings,
    getPhraseForWord,
    setEditorReady,
    getDisplayText,
    displayMode,
    autoScrollEnabled,
    videoRef,
    updateCaptionData,
    changedWordIds,
    markWordsAsChanged,
    newWordIds,
    markWordsAsNew,
    showToast,
    mergeMultipleWords,
    updateViewWord, // Use this for edits to respect translation view
    getViewWordById // Use this to get word by ID respecting translation view
  } = useCaptions();

  useEffect(() => {
    setEditorReady(true);
  }, [setEditorReady, viewCaptionData?.words?.length, viewCaptionData?.skeleton?.length]);
  
  // Use viewCaptionData which respects isTranslationView
  const displayData = viewCaptionData || captionData;

  // O(1) word lookup maps — rebuilt only when the underlying arrays change
  const wordsMap = useMemo(() => {
    const map = {};
    for (const w of displayData.words) { map[w.id] = w; }
    return map;
  }, [displayData.words]);

  const captionWordsMap = useMemo(() => {
    if (captionData.words === displayData.words) return wordsMap;
    const map = {};
    for (const w of captionData.words) { map[w.id] = w; }
    return map;
  }, [captionData.words, displayData.words, wordsMap]);

  const isArabizi = displayMode === 'arabizi';
  const isNonArabic = ['en', 'fr', 'english', 'french', 'arabizi'].includes(projectLanguage);
  const shouldUppercaseText = isNonArabic && themeSettings.uppercaseEverything;
  const formatDisplayText = useCallback((value) => {
    if (!value || typeof value !== 'string') return value;
    return shouldUppercaseText ? value.toLocaleUpperCase() : value;
  }, [shouldUppercaseText]);

  // Speaker features gated by env var
  const speakerFeaturesEnabled = process.env.REACT_APP_ENABLE_SPEAKER_FEATURES === 'true';
  const SPEAKER_COLORS = ['#1e293b', '#9f1239'];

  // Build speaker-change metadata across all words in skeleton order
  const speakerMeta = useMemo(() => {
    if (!speakerFeaturesEnabled) return { speakerChangeIds: new Set(), wordSpeakerColorMap: {} };
    const speakerChangeIds = new Set();
    const wordSpeakerColorMap = {};
    const speakerIndexMap = {};
    let speakerCounter = 0;
    let prevSpeaker = null;

    for (const skel of displayData.skeleton) {
      for (const wid of skel.words) {
        const word = wordsMap[wid];
        if (!word || word.type === 'silence' || word.type === 'punctuation') continue;
        const speaker = word.speaker || null;
        if (speaker !== null) {
          if (!(speaker in speakerIndexMap)) {
            speakerIndexMap[speaker] = speakerCounter++;
          }
          wordSpeakerColorMap[wid] = SPEAKER_COLORS[speakerIndexMap[speaker] % SPEAKER_COLORS.length];
          if (prevSpeaker !== null && speaker !== prevSpeaker) {
            speakerChangeIds.add(wid);
          }
          prevSpeaker = speaker;
        }
      }
    }
    return { speakerChangeIds, wordSpeakerColorMap };
  }, [displayData.skeleton, wordsMap, speakerFeaturesEnabled]);

  // ============== TIME-BASED CHUNK PAGINATION ==============
  const CHUNK_DURATION = 120; // 2 minutes per chunk

  // Group skeletons into time-based chunks
  const timeChunks = useMemo(() => {
    const skeletons = displayData.skeleton;
    if (skeletons.length === 0) return [];

    const totalEnd = Math.max(...skeletons.map(s => s.end_time || 0));
    const numChunks = Math.max(1, Math.ceil(totalEnd / CHUNK_DURATION));
    const chunks = [];

    for (let c = 0; c < numChunks; c++) {
      const chunkStart = c * CHUNK_DURATION;
      const chunkEnd = (c + 1) * CHUNK_DURATION;

      const chunkSkeletons = [];
      skeletons.forEach((skel, globalIdx) => {
        const skelMid = (skel.start_time + skel.end_time) / 2;
        if (skelMid >= chunkStart && skelMid < chunkEnd) {
          chunkSkeletons.push({ skeleton: skel, globalIndex: globalIdx });
        }
      });

      if (chunkSkeletons.length > 0 || c === 0) {
        chunks.push({
          index: chunks.length,
          startTime: chunkStart,
          endTime: chunkEnd,
          skeletons: chunkSkeletons,
          wordCount: chunkSkeletons.reduce((sum, s) => sum + s.skeleton.words.length, 0)
        });
      }
    }

    // If only 1 chunk, skip pagination entirely
    return chunks;
  }, [displayData.skeleton]);

  const needsPagination = timeChunks.length > 1;
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);

  // Clamp chunk index when chunks change
  useEffect(() => {
    if (currentChunkIndex >= timeChunks.length && timeChunks.length > 0) {
      setCurrentChunkIndex(timeChunks.length - 1);
    }
  }, [timeChunks.length, currentChunkIndex]);

  // The visible skeletons for the current chunk (or all if no pagination needed)
  const visibleSkeletonEntries = useMemo(() => {
    if (!needsPagination) {
      return displayData.skeleton.map((skel, idx) => ({ skeleton: skel, globalIndex: idx }));
    }
    const chunk = timeChunks[currentChunkIndex];
    return chunk ? chunk.skeletons : [];
  }, [needsPagination, timeChunks, currentChunkIndex, displayData.skeleton]);

  const currentChunk = timeChunks[currentChunkIndex] || null;

  // Auto-advance chunk when audio plays past boundary
  useEffect(() => {
    if (!needsPagination || !currentChunk) return;
    if (currentTime >= currentChunk.endTime && currentChunkIndex < timeChunks.length - 1) {
      setCurrentChunkIndex(prev => prev + 1);
    } else if (currentTime < currentChunk.startTime && currentChunkIndex > 0) {
      // Find the correct chunk for the current time
      const targetIdx = timeChunks.findIndex(c => currentTime >= c.startTime && currentTime < c.endTime);
      if (targetIdx !== -1 && targetIdx !== currentChunkIndex) {
        setCurrentChunkIndex(targetIdx);
      }
    }
  }, [currentTime, needsPagination, currentChunk, currentChunkIndex, timeChunks]);

  const formatChunkTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Determine current active skeleton and word based on currentTime
  const { activeSkeletonIndex, activeWordId } = useMemo(() => {
    let skelIndex = -1;
    let wordId = null;
    
    for (let i = 0; i < displayData.skeleton.length; i++) {
      const skel = displayData.skeleton[i];
      if (currentTime >= skel.start_time && currentTime < skel.end_time) {
        skelIndex = i;
        break;
      }
    }
    
    for (const word of displayData.words) {
      if (word.type !== 'silence' && 
          currentTime >= word.start_time && 
          currentTime < word.end_time) {
        wordId = word.id;
        break;
      }
    }
    
    return { activeSkeletonIndex: skelIndex, activeWordId: wordId };
  }, [displayData, currentTime]);

  const [selectedWord, setSelectedWord] = useState(null);
  const [selectedWordRect, setSelectedWordRect] = useState(null);
  const [selectedSkeletonIndex, setSelectedSkeletonIndex] = useState(null);
  const [lineSettingsOpen, setLineSettingsOpen] = useState(null);
  const [lineSettingsRect, setLineSettingsRect] = useState(null);
  const [hiddenSkeletons, setHiddenSkeletons] = useState(new Set());
  
  // Multi-word timing selection state
  const [timingSelectionWordIds, setTimingSelectionWordIds] = useState([]);
  const [timingSelectionStartWordId, setTimingSelectionStartWordId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMergeWordsModal, setShowMergeWordsModal] = useState(false);
  
  // Context menu state for right-click
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuAnchorRef = useRef(null);
  
  // Segment playback state
  const [playingSegmentEndTime, setPlayingSegmentEndTime] = useState(null);

  // Timing adjustment modal state
  const [timingAdjustMode, setTimingAdjustMode] = useState('reassign'); // 'reassign' | 'proportional'
  const [audioSegments, setAudioSegments] = useState([]);
  const [wordAssignments, setWordAssignments] = useState({});
  const [selectedWordForPopup, setSelectedWordForPopup] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [playingSegmentId, setPlayingSegmentId] = useState(null);
  const [draggedWordId, setDraggedWordId] = useState(null);
  const [dragOverSegmentId, setDragOverSegmentId] = useState(null);
  const dragImageRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const contentRef = useRef(null);
  const lineRefs = useRef({});
  const wordRefsMap = useRef({}); // Store refs to word elements for search scroll

  const { enableAutoLineBreak, wordsPerLine, showPunctuation } = themeSettings;

  const updateGhostPosition = useCallback((clientX, clientY) => {
    if (!dragImageRef.current || clientX === 0 || clientY === 0) return;
    dragImageRef.current.style.left = `${clientX - dragOffsetRef.current.x}px`;
    dragImageRef.current.style.top = `${clientY - dragOffsetRef.current.y}px`;
  }, []);

  const cleanupDragGhost = useCallback(() => {
    if (dragImageRef.current) {
      dragImageRef.current.remove();
      dragImageRef.current = null;
    }
  }, []);

  // Stop segment playback when reaching end time
  useEffect(() => {
    if (playingSegmentEndTime === null) return;
    
    if (currentTime >= playingSegmentEndTime) {
      videoRef?.current?.pause();
      setPlayingSegmentEndTime(null);
    }
  }, [playingSegmentEndTime, currentTime, videoRef]);

  // Reset segment playback on manual pause
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;
    
    const handlePause = () => {
      setPlayingSegmentEndTime(null);
      setPlayingSegmentId(null);
    };
    
    video.addEventListener('pause', handlePause);
    return () => video.removeEventListener('pause', handlePause);
  }, [videoRef]);

  // Handle audio segment playback in timing modal
  useEffect(() => {
    if (!playingSegmentId || audioSegments.length === 0) return;
    
    const segment = audioSegments.find(s => s.id === playingSegmentId);
    if (!segment) return;
    
    const video = videoRef?.current;
    if (!video) return;
    
    // Stop when segment ends
    if (currentTime >= segment.endTime) {
      video.pause();
      setPlayingSegmentId(null);
    }
  }, [playingSegmentId, audioSegments, currentTime, videoRef]);

  // Close word popup on click outside
  useEffect(() => {
    if (!selectedWordForPopup) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.timing-word-popup') && !e.target.closest('.timing-reassign-word')) {
        setSelectedWordForPopup(null);
      }
    };

    const handleContextMenuOutside = (e) => {
      if (!e.target.closest('.timing-word-popup') && !e.target.closest('.timing-reassign-word')) {
        setSelectedWordForPopup(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('contextmenu', handleContextMenuOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleContextMenuOutside);
    };
  }, [selectedWordForPopup]);

  // Clean up drag image on unmount
  useEffect(() => {
    return () => {
      if (dragImageRef.current) {
        document.body.removeChild(dragImageRef.current);
        dragImageRef.current = null;
      }
    };
  }, []);

  // Disable text selection while timing modal is open
  useEffect(() => {
    if (showConfirmModal) {
      document.body.classList.add('timing-no-select');
    } else {
      document.body.classList.remove('timing-no-select');
    }
    return () => {
      document.body.classList.remove('timing-no-select');
    };
  }, [showConfirmModal]);

  // Keep drag ghost synced with cursor and clean up on end
  useEffect(() => {
    if (!draggedWordId) return;

    const handleWindowDragOver = (event) => {
      event.preventDefault();
      updateGhostPosition(event.clientX, event.clientY);
    };

    const handleWindowDrop = () => {
      cleanupDragGhost();
      setDraggedWordId(null);
      setDragOverSegmentId(null);
    };

    const handleWindowDragEnd = () => {
      cleanupDragGhost();
      setDraggedWordId(null);
      setDragOverSegmentId(null);
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);
    window.addEventListener('dragend', handleWindowDragEnd);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
      window.removeEventListener('dragend', handleWindowDragEnd);
    };
  }, [draggedWordId, updateGhostPosition, cleanupDragGhost]);

  // Scroll to current search match when it changes — navigate to correct chunk first
  useEffect(() => {
    if (!currentSearchMatchId) return;

    // If paginated, find which chunk contains this word and switch to it
    if (needsPagination) {
      const word = wordsMap[currentSearchMatchId];
      if (word) {
        const targetIdx = timeChunks.findIndex(c => word.start_time >= c.startTime && word.start_time < c.endTime);
        if (targetIdx !== -1 && targetIdx !== currentChunkIndex) {
          setCurrentChunkIndex(targetIdx);
          // Wait for the chunk to render before scrolling
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (wordRefsMap.current[currentSearchMatchId]) {
                wordRefsMap.current[currentSearchMatchId].scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            });
          });
          return;
        }
      }
    }

    if (wordRefsMap.current[currentSearchMatchId]) {
      wordRefsMap.current[currentSearchMatchId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [currentSearchMatchId, needsPagination, wordsMap, timeChunks, currentChunkIndex]);

  // Keep word popup anchored to the word while scrolling/resizing
  useEffect(() => {
    if (!selectedWord) return;
    let timeoutId = null;

    const updateRect = () => {
      const wordEl = wordRefsMap.current[selectedWord];
      if (!wordEl) return;
      const rect = wordEl.getBoundingClientRect();
      setSelectedWordRect(rect);
    };

    const scheduleUpdate = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        timeoutId = null;
        updateRect();
      }, 80);
    };

    updateRect();
    const scrollEl = document.getElementById('editor-scroll');
    if (scrollEl) {
      scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true });
    }
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', scheduleUpdate);
      }
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [selectedWord]);

  // Auto-select word when coming from prose view timing button
  useEffect(() => {
    if (autoSelectWordId) {
      // Find the skeleton index for this word
      const skeletonIndex = displayData.skeleton.findIndex(skel => 
        skel.words.includes(autoSelectWordId)
      );
      
      if (skeletonIndex !== -1) {
        // If paginated, navigate to the correct chunk first
        if (needsPagination) {
          const word = wordsMap[autoSelectWordId];
          if (word) {
            const targetIdx = timeChunks.findIndex(c => word.start_time >= c.startTime && word.start_time < c.endTime);
            if (targetIdx !== -1 && targetIdx !== currentChunkIndex) {
              setCurrentChunkIndex(targetIdx);
            }
          }
        }

        // Wait for next frame to ensure refs are populated
        requestAnimationFrame(() => {
          const wordEl = wordRefsMap.current[autoSelectWordId];
          if (wordEl) {
            const rect = wordEl.getBoundingClientRect();
            setSelectedWord(autoSelectWordId);
            setSelectedWordRect(rect);
            setSelectedSkeletonIndex(skeletonIndex);
            
            // Scroll to the word
            wordEl.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
          
          // Clear the auto-select
          if (onAutoSelectHandled) {
            onAutoSelectHandled();
          }
        });
      }
    }
  }, [autoSelectWordId, displayData.skeleton, onAutoSelectHandled, needsPagination, wordsMap, timeChunks, currentChunkIndex]);

  const isPunctuationEnabled = showPunctuation !== false;

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.word-popup') && !e.target.closest('.word-clickable')) {
        setSelectedWord(null);
        setSelectedSkeletonIndex(null);
      }
      if (!e.target.closest('.line-settings-popup') && !e.target.closest('.line-settings-btn')) {
        setLineSettingsOpen(null);
      }
      // Clear timing selection when clicking outside
      if (!e.target.closest('.word-clickable') && !e.target.closest('.timing-selection-bar') && !e.target.closest('.timing-confirm-overlay')) {
        setTimingSelectionWordIds([]);
        setTimingSelectionStartWordId(null);
      }
      // Close context menu when clicking outside
      if (!e.target.closest('.word-context-menu')) {
        setContextMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track scroll for context menu position
  useEffect(() => {
    if (!contextMenu || !contextMenuAnchorRef.current) return;

    let lastUpdate = 0;
    const throttleMs = 80;

    const updateContextMenuPosition = () => {
      const now = Date.now();
      if (now - lastUpdate < throttleMs) return;
      lastUpdate = now;

      const el = contextMenuAnchorRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const topBarOffset = 72;
      setContextMenu(prev => prev ? {
        ...prev,
        x: rect.left + rect.width / 2,
        y: Math.max(rect.bottom + 4, topBarOffset)
      } : null);
    };

    const scrollContainer = document.getElementById('editor-scroll');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', updateContextMenuPosition);
    }
    window.addEventListener('scroll', updateContextMenuPosition);
    window.addEventListener('resize', updateContextMenuPosition);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', updateContextMenuPosition);
      }
      window.removeEventListener('scroll', updateContextMenuPosition);
      window.removeEventListener('resize', updateContextMenuPosition);
    };
  }, [contextMenu]);

  // Autoscroll to center active line
  useEffect(() => {
    if (!autoScrollEnabled || activeSkeletonIndex === -1) return;
    
    const activeLineElement = lineRefs.current[activeSkeletonIndex];
    // Find the scroll container by ID from App.js
    const container = document.getElementById('editor-scroll');
    
    if (activeLineElement && container) {
      // Calculate visible area (exclude bottom ~140px for audio player)
      const containerHeight = container.clientHeight;
      const playerHeight = 140; // Approximate height of floating player + margin
      const visibleHeight = containerHeight - playerHeight;
      
      // Get element position relative to container
      const elementTop = activeLineElement.offsetTop;
      const elementHeight = activeLineElement.offsetHeight;
      
      // Calculate target scroll position to center element in VISIBLE area
      // Target = elementTop - (half of visible space) + (half of element height)
      const scrollTarget = elementTop - (visibleHeight / 2) + (elementHeight / 2);
      
      container.scrollTo({
        top: Math.max(0, scrollTarget),
        behavior: 'smooth'
      });
    }
  }, [activeSkeletonIndex, autoScrollEnabled]);

  // Get a flat list of all word IDs in order across all skeletons
  const allWordIdsInOrder = useMemo(() => {
    const ids = [];
    displayData.skeleton.forEach(skeleton => {
      if (!skeleton.disabled) {
        skeleton.words.forEach(wid => {
          const word = wordsMap[wid];
          if (word && word.type !== 'silence') {
            ids.push(wid);
          }
        });
      }
    });
    return ids;
  }, [displayData.skeleton, wordsMap]);

  // Get all word IDs between two word IDs (inclusive), across all skeletons
  const getWordIdsBetween = (startId, endId) => {
    const startIndex = allWordIdsInOrder.indexOf(startId);
    const endIndex = allWordIdsInOrder.indexOf(endId);
    
    if (startIndex === -1 || endIndex === -1) return [];
    
    const minIdx = Math.min(startIndex, endIndex);
    const maxIdx = Math.max(startIndex, endIndex);
    
    return allWordIdsInOrder.slice(minIdx, maxIdx + 1);
  };

  // Check if we're in timing selection mode (has a start word selected)
  const isInTimingSelectionMode = timingSelectionStartWordId !== null;

  const handleWordClick = (e, wordId, skeletonIndex) => {
    e.stopPropagation();
    const rect = e.target.getBoundingClientRect();
    
    // SHIFT+CLICK: Timing selection mode
    if (e.shiftKey) {
      // If we already have a start word, extend/update the range
      if (isInTimingSelectionMode) {
        const selectedIds = getWordIdsBetween(timingSelectionStartWordId, wordId);
        if (selectedIds.length > 0) {
          setTimingSelectionWordIds(selectedIds);
          // Close any popup
          setSelectedWord(null);
          setSelectedSkeletonIndex(null);
          return;
        }
      }
      
      // First shift+click: start timing selection - reset confirm modal
      setShowConfirmModal(false);
      setTimingSelectionStartWordId(wordId);
      setTimingSelectionWordIds([wordId]);
      // Close any popup when starting selection
      setSelectedWord(null);
      setSelectedSkeletonIndex(null);
      return;
    }
    
    // NORMAL CLICK: Show popup, clear any timing selection
    setShowConfirmModal(false);
    setTimingSelectionWordIds([]);
    setTimingSelectionStartWordId(null);
    
    // Open word popup
    setSelectedWord(wordId);
    setSelectedSkeletonIndex(skeletonIndex);
    setSelectedWordRect(rect);
  };

  // Right-click context menu handler
  const handleWordContextMenu = (e, wordId, skeletonIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Close any open popup
    setSelectedWord(null);
    setSelectedSkeletonIndex(null);
    
    // Store ref to the word element for scroll tracking
    contextMenuAnchorRef.current = e.target;
    
    // Open context menu below the word (but not above top bar)
    const rect = e.target.getBoundingClientRect();
    const topBarOffset = 72;
    setContextMenu({
      x: rect.left + rect.width / 2,
      y: Math.max(rect.bottom + 4, topBarOffset),
      wordId,
      skeletonIndex
    });
  };

  const handleToggleHidden = (skeletonIndex) => {
    setHiddenSkeletons(prev => {
      const next = new Set(prev);
      if (next.has(skeletonIndex)) {
        next.delete(skeletonIndex);
      } else {
        next.add(skeletonIndex);
      }
      return next;
    });
  };

  const handleLineClick = (skeleton, e) => {
    if (e.target.closest('.skeleton-actions') ||
        e.target.closest('.word-clickable') ||
        e.target.closest('.time-control')) {
      return;
    }
    seekToTime(skeleton.start_time);
  };

  const handleTimeChange = (skeletonIndex, field, value) => {
    const skeleton = displayData.skeleton[skeletonIndex];
    if (!skeleton) return;
    
    const newStart = field === 'startTime' ? value : skeleton.start_time;
    const newEnd = field === 'endTime' ? value : skeleton.end_time;
    
    updateSkeletonTiming(skeletonIndex, newStart, newEnd);
  };

  const playSegment = (start, skeletonIndex) => {
    const video = videoRef?.current;
    const skeleton = displayData.skeleton[skeletonIndex];
    if (!video || !skeleton) return;
    
    // Get words in this chunk
    const chunkWords = skeleton.words
      .map(wid => wordsMap[wid])
      .filter(Boolean);
    
    if (chunkWords.length === 0) {
      // Fallback to skeleton timing if no words
      video.currentTime = skeleton.start_time;
      setPlayingSegmentEndTime(skeleton.end_time);
    } else {
      // Use first word's start_time and last word's end_time
      const firstWord = chunkWords[0];
      const lastWord = chunkWords[chunkWords.length - 1];
      video.currentTime = firstWord.start_time;
      setPlayingSegmentEndTime(lastWord.end_time);
    }
    
    video.play();
  };

  // Process words to handle punctuation and silences (uses wordsMap for O(1) lookups)
  const processWords = useCallback((wordIds) => {
    const allWords = [];
    for (const id of wordIds) {
      const w = wordsMap[id];
      if (w) allWords.push(w);
    }
    const displayItems = [];
    
    for (let i = 0; i < allWords.length; i++) {
      const word = allWords[i];
      
      if (word.type === 'silence') continue;
      if (word.type === 'punctuation' && word.attaches_to === 'previous') continue;
      
      const nextWord = allWords[i + 1];
      const attachedPunctuation = (isPunctuationEnabled && nextWord?.type === 'punctuation' && nextWord?.attaches_to === 'previous') 
        ? { id: nextWord.id, text: nextWord.text }
        : null;
      
      const phrase = getPhraseForWord(word.id);
      const phraseInfo = phrase ? {
        phraseId: phrase.id,
        wordIds: phrase.currentWordIds,
        isFirstInPhrase: phrase.currentWordIds[0] === word.id,
        isLastInPhrase: phrase.currentWordIds[phrase.currentWordIds.length - 1] === word.id,
        hasAlternatives: phrase.alternatives && phrase.alternatives.length > 0,
        isLatinPhrase: phrase.activeAlternativeId === 'alt_code_switched'
      } : null;
      
      displayItems.push({
        ...word,
        attachedPunctuation,
        hasBreak: word.break === true,
        phraseInfo
      });
    }
    
    return displayItems;
  }, [wordsMap, isPunctuationEnabled, getPhraseForWord]);

  const shouldShowAutoBreak = (wordIndex, totalWords, hasManualBreak) => {
    if (hasManualBreak) return false;
    if (!enableAutoLineBreak) return false;
    if (wordIndex >= totalWords - 1) return false;
    return wordIndex === wordsPerLine - 1;
  };

  return (
    <div ref={contentRef} className="space-y-4">
        {/* Chunk Navigation Bar */}
        {needsPagination && (
          <div className="chunk-nav-bar">
            <button
              className="chunk-nav-btn"
              disabled={currentChunkIndex === 0}
              onClick={() => {
                const prevIdx = Math.max(0, currentChunkIndex - 1);
                setCurrentChunkIndex(prevIdx);
                seekToTime(timeChunks[prevIdx].startTime);
                document.getElementById('editor-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="rtl:-scale-x-100"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div className="chunk-nav-pills">
              {timeChunks.map((chunk, idx) => (
                <button
                  key={idx}
                  className={`chunk-nav-pill ${idx === currentChunkIndex ? 'active' : ''} ${
                    currentTime >= chunk.startTime && currentTime < chunk.endTime ? 'has-playhead' : ''
                  }`}
                  onClick={() => {
                    setCurrentChunkIndex(idx);
                    seekToTime(chunk.startTime);
                    document.getElementById('editor-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {formatChunkTime(chunk.startTime)}
                </button>
              ))}
            </div>
            <button
              className="chunk-nav-btn"
              disabled={currentChunkIndex >= timeChunks.length - 1}
              onClick={() => {
                const nextIdx = Math.min(timeChunks.length - 1, currentChunkIndex + 1);
                setCurrentChunkIndex(nextIdx);
                seekToTime(timeChunks[nextIdx].startTime);
                document.getElementById('editor-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="rtl:-scale-x-100"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
        )}

        {visibleSkeletonEntries.map(({ skeleton, globalIndex: skeletonIndex }) => {
          const displayWords = processWords(skeleton.words);
        if (displayWords.length === 0) {
          return null;
        }
        
        const isActive = activeSkeletonIndex === skeletonIndex;
        const isHidden = hiddenSkeletons.has(skeletonIndex);
          
          return (
            <React.Fragment key={skeleton.id || skeletonIndex}>
            {/* Skeleton Card */}
              <div
                ref={(el) => lineRefs.current[skeletonIndex] = el}
              id={`sub-${skeleton.id}`}
              className={`group relative rounded-2xl transition-shadow duration-200 ${
                isActive 
                  ? 'bg-white shadow-[0_12px_40px_-12px_rgba(0,0,0,0.08)] z-10 ring-1 ring-brand/20'
                    : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'
              } ${isHidden ? 'opacity-60' : ''} ${skeleton.disabled ? 'opacity-40' : ''}`}
                onClick={(e) => handleLineClick(skeleton, e)}
            >
              <div className="flex gap-3 p-3 items-center">
                {/* Time Controls */}
                <div className="hidden sm:flex flex-col gap-2 shrink-0 time-control">
                  <TimeControl 
                    time={skeleton.start_time} 
                    onChange={(val) => handleTimeChange(skeletonIndex, 'startTime', val)} 
                    isActive={isActive} 
                    label={t('START')} 
                  />
                  <TimeControl 
                    time={skeleton.end_time} 
                    onChange={(val) => handleTimeChange(skeletonIndex, 'endTime', val)} 
                    isActive={isActive} 
                    label={t('END')} 
                  />
                </div>

                {/* Words */}
                <div 
                  className={`words-container flex-1 min-w-0 font-medium leading-loose py-2 px-3 rounded-lg border border-transparent ${
                    isActive ? 'text-slate-900' : 'text-slate-700'
                  } ${(isArabizi || isTranslationView || isNonArabic) ? 'text-left' : 'text-center'}`}
                  dir={(isArabizi || isTranslationView || isNonArabic) ? 'ltr' : 'rtl'}
                  style={{ 
                    fontFamily: (isTranslationView || isNonArabic) ? (themeSettings.latinFontFamily || 'Inter, system-ui, sans-serif') : themeSettings.proseFontFamily,
                    fontSize: `${themeSettings.proseFontSize || 1.25}rem`
                  }}
                >
                  {(() => {
                    const renderWordSpan = (word) => {
                      const isProtectedWord = !!word.is_protected;
                      const phraseStyle = (word.phraseInfo && !isNonArabic) ? {
                        borderBottom: word.phraseInfo.hasAlternatives 
                          ? '2px solid #22c55e'
                          : '2px dotted #4ade80',
                        paddingBottom: '2px',
                        unicodeBidi: 'plaintext'
                      } : {};

                      const protectedStyle = isProtectedWord ? {
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        pointerEvents: 'none',
                        opacity: 0.65
                      } : {};

                      const speakerColorStyle = (themeSettings.speakerColorEnabled && speakerMeta.wordSpeakerColorMap[word.id] && activeWordId !== word.id)
                        ? { color: speakerMeta.wordSpeakerColorMap[word.id] }
                        : {};

                      const isAutoSpeakerChange = themeSettings.speakerDashEnabled && speakerMeta.speakerChangeIds.has(word.id);
                      const showSpeakerDash = isAutoSpeakerChange && word.speakerDash !== false;

                      return (
                        <span
                          ref={(el) => { wordRefsMap.current[word.id] = el; }}
                          onClick={isProtectedWord ? undefined : (e) => handleWordClick(e, word.id, skeletonIndex)}
                          onContextMenu={isProtectedWord ? (e) => e.preventDefault() : (e) => handleWordContextMenu(e, word.id, skeletonIndex)}
                          onDoubleClick={isProtectedWord ? undefined : (e) => {
                            e.stopPropagation();
                            if (word.start_time !== undefined) {
                              seekToTime(word.start_time);
                            }
                          }}
                          className={`word-clickable hover:bg-brand/10 hover:text-brand rounded px-1 cursor-pointer ${
                            activeWordId === word.id ? 'word-active bg-brand text-white' : ''
                          } ${selectedWord === word.id ? 'popup-active' : ''} ${
                            contextMenu?.wordId === word.id ? 'context-menu-active' : ''
                          } ${searchMatchIds.has(word.id) ? 'search-match' : ''
                          } ${currentSearchMatchId === word.id ? 'search-match-current' : ''} ${
                            timingSelectionWordIds.includes(word.id) ? 'timing-selected' : ''
                          } ${
                            changedWordIds.has(word.id) ? 'word-just-changed' : ''
                          } ${
                            newWordIds.has(word.id) ? 'word-new' : ''
                          }`}
                          style={{ ...phraseStyle, ...protectedStyle, ...speakerColorStyle }}
                          dir={word.phraseInfo?.isLatinPhrase || isArabizi || isTranslationView || isNonArabic ? 'ltr' : 'rtl'}
                        >
                          {showSpeakerDash && <span className="speaker-dash" style={{ opacity: 0.5 }}>– </span>}
                          {formatDisplayText(getDisplayText(word))}{word.attachedPunctuation ? formatDisplayText(getDisplayText(word.attachedPunctuation)) : ''}
                        </span>
                      );
                    };

                    const getWordText = (word) => `${getDisplayText(word)}${word.attachedPunctuation ? getDisplayText(word.attachedPunctuation) : ''}`;
                    const isLatinWord = (word) => {
                      if (word.phraseInfo?.isLatinPhrase) return true;
                      const text = getWordText(word);
                      const hasLatin = /[A-Za-z]/.test(text);
                      const hasArabic = /[\u0600-\u06FF]/.test(text);
                      return hasLatin && !hasArabic;
                    };

                    const renderWordWithBreak = (word, index) => {
                      const showManualBreak = word.hasBreak && index < displayWords.length - 1;
                      const showAutoBreak = shouldShowAutoBreak(index, displayWords.length, word.hasBreak);

                      return (
                        <React.Fragment key={word.id}>
                          {renderWordSpan(word)}
                          {showManualBreak && (
                            <span className="text-brand/40 text-xs">↵</span>
                          )}
                          {showAutoBreak && (
                            <span className="text-slate-300 text-xs">↵</span>
                          )}
                        </React.Fragment>
                      );
                    };

                    const runs = [];
                    for (let i = 0; i < displayWords.length; i++) {
                      const word = displayWords[i];
                      const latin = isLatinWord(word);
                      const lastRun = runs[runs.length - 1];
                      if (!lastRun || lastRun.isLatin !== latin) {
                        runs.push({ isLatin: latin, items: [{ word, index: i }] });
                      } else {
                        lastRun.items.push({ word, index: i });
                      }
                    }

                    return runs.map((run, runIdx) => (
                      <React.Fragment key={`${runIdx}-${run.items[0]?.word.id}`}>
                        {run.isLatin ? (
                          <span 
                            dir="ltr" 
                            style={{ 
                              unicodeBidi: 'isolate',
                              fontFamily: themeSettings.latinFontFamily || 'Inter'
                            }}
                          >
                            {run.items.map(({ word, index }, itemIdx) => (
                              <React.Fragment key={word.id}>
                                {renderWordWithBreak(word, index)}
                                {itemIdx < run.items.length - 1 && ' '}
                              </React.Fragment>
                            ))}
                          </span>
                        ) : (
                          run.items.map(({ word, index }) => (
                            <React.Fragment key={word.id}>
                              {renderWordWithBreak(word, index)}
                              {' '}
                            </React.Fragment>
                          ))
                        )}
                        {' '}
                      </React.Fragment>
                    ));
                  })()}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity skeleton-actions">
                  <button 
                    onClick={() => playSegment(skeleton.start_time, skeletonIndex)} 
                    className="p-2 text-slate-300 hover:text-brand hover:bg-brand/5 rounded-xl transition-colors tooltip-trigger"
                    data-tooltip={t('Play segment')}
                    aria-label={t('Play segment')}
                  >
                    <PlayIcon />
                  </button>
                  <button 
                    onClick={() => handleToggleHidden(skeletonIndex)}
                    className={`p-2 rounded-xl transition-colors tooltip-trigger ${
                      isHidden 
                        ? 'text-brand bg-brand/10' 
                        : 'text-slate-300 hover:text-slate-600 hover:bg-slate-100'
                    }`}
                    data-tooltip={t(isHidden ? 'Show' : 'Hide')}
                    aria-label={t(isHidden ? 'Show' : 'Hide')}
                  >
                    {isHidden ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                  <button 
                    onClick={() => deleteSkeleton(skeletonIndex)} 
                    className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors tooltip-trigger"
                    data-tooltip={t('Delete')}
                    aria-label={t('Delete')}
                  >
                    <TrashIcon />
                  </button>
                </div>
                </div>
              </div>

            {/* Add Button Between Cards */}
            <div className="h-4 -my-2 relative group/add z-20 flex justify-center items-center pointer-events-none">
              <div 
                  onClick={() => addSkeleton(skeletonIndex)}
                className="w-full h-full pointer-events-auto cursor-pointer flex items-center justify-center opacity-0 group-hover/add:opacity-100 transition-opacity"
              >
                <div className="w-[80%] h-px bg-slate-100 absolute"></div>
                <div className="bg-white border border-slate-200 text-slate-400 rounded-full p-1 shadow-sm transform scale-75 hover:scale-100 hover:border-brand hover:text-brand transition-all relative z-10">
                  <PlusIcon />
                </div>
              </div>
              </div>
            </React.Fragment>
          );
        })}

      {/* Bottom Chunk Navigation */}
      {needsPagination && visibleSkeletonEntries.length > 0 && (
        <div className="chunk-nav-bottom">
          {currentChunkIndex > 0 && (
            <button
              className="chunk-nav-jump"
              onClick={() => {
                const prevIdx = currentChunkIndex - 1;
                setCurrentChunkIndex(prevIdx);
                seekToTime(timeChunks[prevIdx].startTime);
                document.getElementById('editor-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="rtl:-scale-x-100"><polyline points="15 18 9 12 15 6" /></svg>
              {t('Previous')} (<bdi dir="ltr">{formatChunkTime(timeChunks[currentChunkIndex - 1]?.startTime || 0)} – {formatChunkTime(timeChunks[currentChunkIndex - 1]?.endTime || 0)}</bdi>)
            </button>
          )}
          {currentChunkIndex < timeChunks.length - 1 && (
            <button
              className="chunk-nav-jump"
              onClick={() => {
                const nextIdx = currentChunkIndex + 1;
                setCurrentChunkIndex(nextIdx);
                seekToTime(timeChunks[nextIdx].startTime);
                document.getElementById('editor-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {t('Next')} (<bdi dir="ltr">{formatChunkTime(timeChunks[currentChunkIndex + 1]?.startTime || 0)} – {formatChunkTime(timeChunks[currentChunkIndex + 1]?.endTime || 0)}</bdi>)
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="rtl:-scale-x-100"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          )}
        </div>
      )}

      {/* Add First Card Button (when empty) - only in original view */}
        {displayData.skeleton.length === 0 && !isTranslationView && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <button
              onClick={() => addSkeleton(-1)}
            className="w-16 h-16 rounded-full bg-slate-100 hover:bg-brand hover:text-white text-slate-400 flex items-center justify-center transition-all hover:scale-110"
            >
            <PlusIcon />
            </button>
          <p className="mt-4 text-slate-400 font-medium">{t('Add your first caption')}</p>
          </div>
        )}

      {/* Trial Protection End Message */}
      {isTrialProtected && displayData.skeleton.length > 0 && (
        <div className="mt-8 mb-4">
          <div className="flex flex-col items-center justify-center py-8 px-6 border-2 border-brand/15 rounded-2xl" style={{ backgroundColor: '#fff0f1' }}>
            <div className="w-12 h-12 rounded-full bg-brand flex items-center justify-center mb-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">
              {t('Verify your number to continue')}
            </h3>
            <p className="text-sm text-slate-600 text-center mb-4 max-w-md">
              {t('Verify your phone number to unlock the full transcript and continue editing.')}
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onVerifyClick && onVerifyClick()}
                className="px-6 py-2.5 bg-brand hover:bg-brand-dark text-white text-sm font-bold rounded-lg transition-all active:scale-[0.98] shadow-sm"
              >
                {t('Verify Now')}
              </button>
              <button
                onClick={() => onUpgradeClick && onUpgradeClick()}
                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-lg transition-all active:scale-[0.98] shadow-sm"
              >
                {t('Upgrade')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add at End Button - only in original view */}
      {displayData.skeleton.length > 0 && !isTranslationView && !isTrialProtected && (
        <div className="mt-8 pt-4 border-t border-slate-50 flex justify-center">
          <button 
            onClick={() => addSkeleton(displayData.skeleton.length - 1)} 
            className="flex items-center gap-2 px-8 py-3 text-sm font-bold text-slate-500 border-2 border-dashed border-slate-200 rounded-full hover:border-brand hover:text-brand transition-all"
          >
            <PlusIcon />
            {t('Add segment')}
          </button>
      </div>
      )}

      {/* Word Popup */}
      {selectedWord && selectedWordRect && selectedSkeletonIndex !== null && (
        <WordPopup
          wordId={selectedWord}
          skeletonIndex={selectedSkeletonIndex}
          anchorRect={selectedWordRect}
          isNonArabic={isNonArabic}
          onClose={() => {
            setSelectedWord(null);
            setSelectedSkeletonIndex(null);
          }}
        />
      )}

      {/* Line Settings Popup */}
      {lineSettingsOpen !== null && lineSettingsRect && (
        <LineSettingsPopup
          skeletonIndex={lineSettingsOpen}
          anchorRect={lineSettingsRect}
          onClose={() => setLineSettingsOpen(null)}
        />
      )}

      {/* Word Context Menu (Right-click) */}
      {contextMenu && createPortal(
        <div 
          className="word-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 10000
          }}
        >
          <button
            className="context-menu-item"
            onClick={() => {
              const skeleton = captionData.skeleton[contextMenu.skeletonIndex];
              if (!skeleton) return;
              
              const wordIndex = skeleton.words.indexOf(contextMenu.wordId);
              const currentWord = captionWordsMap[contextMenu.wordId];
              
              // Generate new word
              const newWordId = `w${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const newWord = {
                id: newWordId,
                text: 'جديد',
                type: 'word',
                start_time: currentWord?.start_time ? currentWord.start_time - 0.25 : 0,
                end_time: currentWord?.start_time || 0.5,
                highlightColor: null,
                break: false
              };
              
              // Insert before current word
              const newWordIds = [
                ...skeleton.words.slice(0, wordIndex),
                newWordId,
                ...skeleton.words.slice(wordIndex)
              ];
              
              const updatedWords = [...captionData.words, newWord].sort((a, b) =>
                (a.start_time || 0) - (b.start_time || 0)
              );
              
              const updatedWordsMap = {};
              for (const w of updatedWords) { updatedWordsMap[w.id] = w; }
              const firstWord = updatedWordsMap[newWordIds[0]];
              const lastWord = updatedWordsMap[newWordIds[newWordIds.length - 1]];
              
              const newSkeleton = captionData.skeleton.map((skel, idx) =>
                idx === contextMenu.skeletonIndex ? {
                  ...skel,
                  words: newWordIds,
                  start_time: firstWord?.start_time ?? skel.start_time,
                  end_time: lastWord?.end_time ?? skel.end_time
                } : skel
              );
              
              updateCaptionData({ skeleton: newSkeleton, words: updatedWords });
              markWordsAsChanged(newWordId);
              markWordsAsNew(newWordId);
              setContextMenu(null);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{t('Add word before')}</span>
          </button>
          <button
            className="context-menu-item"
            onClick={() => {
              addWord(contextMenu.skeletonIndex, contextMenu.wordId, 'جديد');
              setContextMenu(null);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{t('Add word after')}</span>
          </button>
          <div className="context-menu-divider"></div>
          <button
            className={`context-menu-item danger ${
              captionData.skeleton[contextMenu.skeletonIndex]?.words.length <= 1 ? 'disabled' : ''
            }`}
            onClick={() => {
              const skeleton = captionData.skeleton[contextMenu.skeletonIndex];
              if (skeleton && skeleton.words.length > 1) {
                removeWord(contextMenu.skeletonIndex, contextMenu.wordId);
              }
              setContextMenu(null);
            }}
            disabled={captionData.skeleton[contextMenu.skeletonIndex]?.words.length <= 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            <span>{t('Remove word')}</span>
          </button>
        </div>,
        document.body
      )}

      {/* Timing Selection Guide - Show throughout selection process */}
      {timingSelectionWordIds.length >= 1 && timingSelectionStartWordId && (
        <div className="timing-selection-bar guide">
          <div className="timing-selection-icon pulse">
            <ClockIcon />
          </div>
          <div className="timing-selection-info">
            <span className="timing-selection-count">
              🎯 {t('Sync Out-of-Sync Words')} {timingSelectionWordIds.length >= 2 && t('({count} selected)', { count: timingSelectionWordIds.length })}
            </span>
            <span className="timing-selection-hint">
              {rich(t('<b>Shift+click</b> to select all words that are out of sync in a sequence'), { b: (c) => <strong>{c}</strong> })}
            </span>
          </div>
          {timingSelectionWordIds.length >= 2 && (
            <button 
              className="timing-selection-btn primary"
              onClick={() => setShowConfirmModal(true)}
            >
              <ClockIcon />
              {t('Adjust Timing')}
            </button>
          )}
          {timingSelectionWordIds.length >= 2 && (
          <button 
              className="timing-selection-btn"
              onClick={() => setShowMergeWordsModal(true)}
            >
              {t('Merge Words')}
            </button>
          )}
          <button 
            className="timing-selection-btn cancel tooltip-trigger"
            onClick={() => {
              setShowConfirmModal(false);
              setShowMergeWordsModal(false);
              setTimingSelectionWordIds([]);
              setTimingSelectionStartWordId(null);
            }}
            data-tooltip={t('Cancel selection')}
            aria-label={t('Cancel selection')}
          >
            <XIcon />
          </button>
        </div>
      )}

      {/* Merge Words Modal */}
      {showMergeWordsModal && timingSelectionWordIds.length >= 2 && (
        <div className="timing-confirm-overlay" onClick={() => setShowMergeWordsModal(false)}>
          <div className="timing-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="timing-confirm-header">
              <span className="timing-confirm-icon">🧩</span>
              <h3>{t('Merge Selected Words')}</h3>
            </div>
            <p className="timing-confirm-text">
              {t('This will combine the selected words into a single word inside the same chunk.')}
            </p>
            <div className={`timing-confirm-sequence ${(displayMode === 'arabizi' || isTranslationView || isNonArabic) ? 'ltr' : 'rtl'}`}>
              {timingSelectionWordIds.map((wordId) => {
                const word = wordsMap[wordId];
                return (
                  <span key={wordId} className="timing-confirm-word">
                    {getDisplayText(word)}
                  </span>
                );
              })}
            </div>
            <div className="timing-confirm-actions">
              <button className="timing-confirm-btn cancel" onClick={() => setShowMergeWordsModal(false)}>
                {t('Cancel')}
              </button>
              <button 
                className="timing-confirm-btn confirm"
                onClick={() => {
                  const orderedIds = [...timingSelectionWordIds].sort((a, b) => {
                    const wa = captionWordsMap[a];
                    const wb = captionWordsMap[b];
                    return (wa?.start_time ?? 0) - (wb?.start_time ?? 0);
                  });
                  const result = mergeMultipleWords(0, orderedIds);
                  if (result?.success) {
                    showToast?.('Words merged.', 'success');
                    setShowMergeWordsModal(false);
                    setTimingSelectionWordIds([]);
                    setTimingSelectionStartWordId(null);
                  } else {
                    showToast?.(result?.error || 'Merge failed.', 'warning');
                  }
                }}
              >
                {t('Merge Words')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timing Adjustment Modal */}
      {showConfirmModal && timingSelectionWordIds.length >= 2 && (() => {
        // Get selected words data for the modal
        const modalSelectedWords = timingSelectionWordIds
                    .map(id => captionWordsMap[id])
                    .filter(Boolean);
                  
        // Initialize audio segments when modal opens (if not already done)
        if (audioSegments.length === 0 && modalSelectedWords.length > 0) {
          const segments = modalSelectedWords.map((word, index) => ({
            id: `seg_${index}`,
            startTime: word.start_time,
            endTime: word.end_time,
            originalWordId: word.id
          }));
          setAudioSegments(segments);
          
          const assignments = {};
          modalSelectedWords.forEach((word, index) => {
            assignments[word.id] = `seg_${index}`;
          });
          setWordAssignments(assignments);
        }

        const formatTime = (time) => {
          const mins = Math.floor(time / 60);
          const secs = (time % 60).toFixed(2);
          return mins > 0 ? `${mins}:${secs.padStart(5, '0')}` : `${secs}s`;
        };

        const getWordsForSegment = (segmentId) => {
          return modalSelectedWords.filter(w => wordAssignments[w.id] === segmentId);
        };

        const playAudioSegment = (segment) => {
          const video = videoRef?.current;
          if (!video) return;
          video.currentTime = segment.startTime;
          setPlayingSegmentId(segment.id);
          video.play();
        };

        const handleWordPopupClick = (e, wordId) => {
          e.stopPropagation();
          const rect = e.target.getBoundingClientRect();
          const topBarOffset = 72;
          setPopupPosition({
            x: rect.left + rect.width / 2,
            y: Math.max(rect.bottom + 8, topBarOffset + 8)
          });
          setSelectedWordForPopup(wordId);
        };

        const closeWordPopup = () => {
          setSelectedWordForPopup(null);
        };

        // Drag and drop handlers
        const handleDragStart = (e, wordId) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', wordId);
          
          const rect = e.target.getBoundingClientRect();
          const styles = window.getComputedStyle(e.target);
          const backgroundColor =
            styles.backgroundColor && styles.backgroundColor !== 'rgba(0, 0, 0, 0)'
              ? styles.backgroundColor
              : '#f8fafc';
          
          // Store offset from click position to element top-left
          dragOffsetRef.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          };
          
          // Create custom ghost that follows cursor
          const ghost = document.createElement('div');
          ghost.textContent = e.target.textContent;
          ghost.style.cssText = `
            position: fixed;
            left: ${rect.left}px;
            top: ${rect.top}px;
            background-color: ${backgroundColor};
            color: ${styles.color || '#0f172a'};
            padding: ${styles.padding};
            border-radius: ${styles.borderRadius};
            font-size: ${styles.fontSize};
            font-weight: ${styles.fontWeight};
            font-family: ${styles.fontFamily};
            border: ${styles.borderWidth || '1px'} ${styles.borderStyle || 'solid'} ${styles.borderColor || '#e2e8f0'};
            line-height: ${styles.lineHeight};
            z-index: 99999;
            pointer-events: none;
            white-space: nowrap;
          `;
          document.body.appendChild(ghost);
          dragImageRef.current = ghost;
          
          // Hide native drag image with transparent 1x1 pixel
          const emptyImg = new Image();
          emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
          e.dataTransfer.setDragImage(emptyImg, 0, 0);
          
          setDraggedWordId(wordId);
        };
        
        const handleDrag = (e) => {
          updateGhostPosition(e.clientX, e.clientY);
        };

        const handleDragEnd = () => {
          cleanupDragGhost();
          setDraggedWordId(null);
          setDragOverSegmentId(null);
        };

        const handleDragOver = (e, segmentId) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (dragOverSegmentId !== segmentId) {
            setDragOverSegmentId(segmentId);
          }
        };

        const handleDragLeave = (e) => {
          // Only clear if we're leaving the segment entirely
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX;
          const y = e.clientY;
          
          if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setDragOverSegmentId(null);
          }
        };

        const handleDrop = (e, segmentId) => {
          e.preventDefault();
          e.stopPropagation();
          
          const wordId = e.dataTransfer.getData('text/plain') || draggedWordId;
          if (wordId && segmentId && wordAssignments[wordId] !== segmentId) {
            setWordAssignments(prev => ({ ...prev, [wordId]: segmentId }));
          }
          
          cleanupDragGhost();
          setDraggedWordId(null);
          setDragOverSegmentId(null);
        };

        const moveWordUp = (wordId) => {
          const currentSegmentId = wordAssignments[wordId];
          const currentSegmentIndex = audioSegments.findIndex(s => s.id === currentSegmentId);
          if (currentSegmentIndex <= 0) return;
          
          const previousSegmentId = audioSegments[currentSegmentIndex - 1].id;
          setWordAssignments(prev => ({ ...prev, [wordId]: previousSegmentId }));
          closeWordPopup();
        };

        const moveWordDown = (wordId) => {
          const currentSegmentId = wordAssignments[wordId];
          const currentSegmentIndex = audioSegments.findIndex(s => s.id === currentSegmentId);
          if (currentSegmentIndex >= audioSegments.length - 1) return;
          
          const nextSegmentId = audioSegments[currentSegmentIndex + 1].id;
          setWordAssignments(prev => ({ ...prev, [wordId]: nextSegmentId }));
          closeWordPopup();
        };

        const mergeSegments = (segmentIndex) => {
          if (segmentIndex >= audioSegments.length - 1) return;
          
          const currentSegment = audioSegments[segmentIndex];
          const nextSegment = audioSegments[segmentIndex + 1];
          
          const mergedSegment = {
            id: currentSegment.id,
            startTime: currentSegment.startTime,
            endTime: nextSegment.endTime,
            originalWordId: currentSegment.originalWordId
          };
          
          const newSegments = [
            ...audioSegments.slice(0, segmentIndex),
            mergedSegment,
            ...audioSegments.slice(segmentIndex + 2)
          ];
          
          // Update assignments - move words from next segment to current segment
          const newAssignments = { ...wordAssignments };
          Object.keys(newAssignments).forEach(wordId => {
            if (newAssignments[wordId] === nextSegment.id) {
              newAssignments[wordId] = currentSegment.id;
            }
          });
          
          // Get all words that will be in the merged segment
          const wordsInMergedSegment = modalSelectedWords.filter(w => 
            newAssignments[w.id] === currentSegment.id
          );
          
          // If there are multiple words, apply proportional timing immediately
          if (wordsInMergedSegment.length > 1) {
            const totalChars = wordsInMergedSegment.reduce((sum, w) => sum + (getDisplayText(w)?.length || 1), 0);
            const segmentDuration = mergedSegment.endTime - mergedSegment.startTime;
            
            // Calculate proportional timings for each word in the merged segment
            let currentStart = mergedSegment.startTime;
            const mergedWordIdSet = new Set(wordsInMergedSegment.map(w => w.id));
            const mergedWordIndexMap = {};
            wordsInMergedSegment.forEach((w, idx) => { mergedWordIndexMap[w.id] = idx; });

            const updatedWords = captionData.words.map(word => {
              if (!mergedWordIdSet.has(word.id)) return word;
              
              const wordIndex = mergedWordIndexMap[word.id];
              const charRatio = (getDisplayText(word)?.length || 1) / totalChars;
              const wordDuration = segmentDuration * charRatio;
              
              const newWord = {
                ...word,
                start_time: currentStart,
                end_time: wordIndex === wordsInMergedSegment.length - 1 ? mergedSegment.endTime : currentStart + wordDuration
              };
              currentStart += wordDuration;
              return newWord;
            });
            
            // Update skeleton timings
            const updatedWordsLookup = {};
            for (const w of updatedWords) { updatedWordsLookup[w.id] = w; }
            const mergedWordIds = mergedWordIdSet;
            const updatedSkeletons = captionData.skeleton.map(skel => {
              const firstWordId = skel.words[0];
              const lastWordId = skel.words[skel.words.length - 1];
              let newSkel = { ...skel };
              
              const firstWord = updatedWordsLookup[firstWordId];
              const lastWord = updatedWordsLookup[lastWordId];
              
              if (firstWord && mergedWordIds.has(firstWordId)) newSkel.start_time = firstWord.start_time;
              if (lastWord && mergedWordIds.has(lastWordId)) newSkel.end_time = lastWord.end_time;
              
              return newSkel;
            });
                    
            // Apply caption data update
            updateCaptionData({ ...captionData, words: updatedWords, skeleton: updatedSkeletons });
            markWordsAsChanged(wordsInMergedSegment.map(w => w.id));
          }
          
          setAudioSegments(newSegments);
          setWordAssignments(newAssignments);
        };

        const handleProportionalApply = () => {
          if (modalSelectedWords.length >= 2) {
            const startTime = Math.min(...modalSelectedWords.map(w => w.start_time ?? 0));
            const endTime = Math.max(...modalSelectedWords.map(w => w.end_time ?? 0));
            const totalDuration = endTime - startTime;
            const totalChars = modalSelectedWords.reduce((sum, w) => sum + (w.text?.length || 1), 0);
            const sortedSelectedWords = [...modalSelectedWords].sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));
            
                    const newTimings = {};
            let currTime = startTime;
                    sortedSelectedWords.forEach((word, index) => {
                      const charRatio = (word.text?.length || 1) / totalChars;
                      const wordDuration = totalDuration * charRatio;
                      newTimings[word.id] = {
                start_time: currTime,
                end_time: index === sortedSelectedWords.length - 1 ? endTime : currTime + wordDuration
                      };
              currTime += wordDuration;
                    });
                    
            const updatedWords = captionData.words.map(w => newTimings[w.id] ? { ...w, ...newTimings[w.id] } : w);
                    const updatedWordIds = new Set(Object.keys(newTimings));
                    
                    const updatedSkeletons = captionData.skeleton.map(skel => {
                      const firstWordId = skel.words[0];
                      const lastWordId = skel.words[skel.words.length - 1];
                      let newSkel = { ...skel };
              if (updatedWordIds.has(firstWordId)) newSkel.start_time = newTimings[firstWordId].start_time;
              if (updatedWordIds.has(lastWordId)) newSkel.end_time = newTimings[lastWordId].end_time;
              return newSkel;
            });
            
            updateCaptionData({ ...captionData, words: updatedWords, skeleton: updatedSkeletons });
            markWordsAsChanged(Object.keys(newTimings));
          }
          
          handleCloseModal();
        };

        const handleReassignApply = () => {
          const updatedWords = captionData.words.map(word => {
            if (!timingSelectionWordIds.includes(word.id)) return word;
            
            const assignedSegmentId = wordAssignments[word.id];
            const segment = audioSegments.find(s => s.id === assignedSegmentId);
            if (!segment) return word;
            
            const wordsInSegment = modalSelectedWords.filter(w => wordAssignments[w.id] === assignedSegmentId);
            const wordIndex = wordsInSegment.findIndex(w => w.id === word.id);
            
            if (wordsInSegment.length === 1) {
              return { ...word, start_time: segment.startTime, end_time: segment.endTime };
            } else {
              const totalChars = wordsInSegment.reduce((sum, w) => sum + (getDisplayText(w)?.length || 1), 0);
              const segmentDuration = segment.endTime - segment.startTime;
              
              let currentStart = segment.startTime;
              for (let i = 0; i < wordIndex; i++) {
                const w = wordsInSegment[i];
                const charRatio = (getDisplayText(w)?.length || 1) / totalChars;
                currentStart += segmentDuration * charRatio;
                      }
                      
              const charRatio = (getDisplayText(word)?.length || 1) / totalChars;
              const wordDuration = segmentDuration * charRatio;
              
              return {
                ...word,
                start_time: currentStart,
                end_time: wordIndex === wordsInSegment.length - 1 ? segment.endTime : currentStart + wordDuration
              };
            }
          });

          const updatedWordIds = new Set(timingSelectionWordIds);
          const updatedWordsLookup = {};
          for (const w of updatedWords) { updatedWordsLookup[w.id] = w; }
          const updatedSkeletons = captionData.skeleton.map(skel => {
            const firstWordId = skel.words[0];
            const lastWordId = skel.words[skel.words.length - 1];
            let newSkel = { ...skel };
            
            const firstWord = updatedWordsLookup[firstWordId];
            const lastWord = updatedWordsLookup[lastWordId];
            
            if (firstWord && updatedWordIds.has(firstWordId)) newSkel.start_time = firstWord.start_time;
            if (lastWord && updatedWordIds.has(lastWordId)) newSkel.end_time = lastWord.end_time;
                      
                      return newSkel;
                    });
                    
                    updateCaptionData({ ...captionData, words: updatedWords, skeleton: updatedSkeletons });
          markWordsAsChanged(timingSelectionWordIds);
          handleCloseModal();
        };

        const handleCloseModal = () => {
                  setShowConfirmModal(false);
                  setTimingSelectionWordIds([]);
                  setTimingSelectionStartWordId(null);
          setAudioSegments([]);
          setWordAssignments({});
          setTimingAdjustMode('reassign');
          setSelectedWordForPopup(null);
          setPlayingSegmentId(null);
          setDraggedWordId(null);
          setDragOverSegmentId(null);
        };

        // Check if reassign mode has changes
        const hasReassignChanges = Object.keys(wordAssignments).some((wordId, index) => {
          return wordAssignments[wordId] !== `seg_${index}`;
        }) || audioSegments.length !== modalSelectedWords.length;

        const handleModalClick = (e) => {
          e.stopPropagation();
          if (!e.target.closest('.timing-word-popup') && !e.target.closest('.timing-reassign-word')) {
            setSelectedWordForPopup(null);
          }
        };

        return (
          <div className="timing-confirm-overlay" onClick={handleCloseModal}>
            <div className="timing-confirm-modal timing-modal-large" onClick={handleModalClick}>
              <div className="timing-confirm-header">
                <div className="timing-header-left">
                  <span className="timing-confirm-icon">🎯</span>
                  <h3>{t('Adjust Word Timing')}</h3>
                  <span className="timing-word-count">{t('{count} words', { count: modalSelectedWords.length })}</span>
                </div>
                <button className="timing-close-btn" onClick={handleCloseModal} aria-label={t('Close')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Tab Switcher */}
              <div className="timing-tab-switcher">
                <button 
                  className={`timing-tab-btn ${timingAdjustMode === 'reassign' ? 'active' : ''}`}
                  onClick={() => setTimingAdjustMode('reassign')}
                >
                  {t('Reassign Word Timing')}
                </button>
                <button 
                  className={`timing-tab-btn ${timingAdjustMode === 'proportional' ? 'active' : ''}`}
                  onClick={() => setTimingAdjustMode('proportional')}
                >
                  {t('Proportional Adjustment')}
              </button>
            </div>

              {/* Combined text preview */}
              <div className={`timing-text-preview ${(displayMode === 'arabizi' || isTranslationView || isNonArabic) ? 'ltr' : 'rtl'}`}>
                <span className="preview-label">{t('Selected:')}</span>
                <span className="preview-text">
                  {modalSelectedWords.map(w => getDisplayText(w)).join(' ')}
                </span>
          </div>

              {/* Proportional Mode Content */}
              {timingAdjustMode === 'proportional' && (
                <>
                  <div className="timing-proportional-info">
                    <p className="timing-confirm-text">
                      {rich(t('This will redistribute timing <b>proportionally</b> based on word length.'), { b: (c) => <strong>{c}</strong> })}
                    </p>
                    <div className={`timing-confirm-sequence ${(displayMode === 'arabizi' || isTranslationView || isNonArabic) ? 'ltr' : 'rtl'}`}>
                      {timingSelectionWordIds.map((wordId) => {
                        const word = wordsMap[wordId];
                        return (
                          <span key={wordId} className="timing-confirm-word">
                            {getDisplayText(word)}
                          </span>
                        );
                      })}
        </div>
                    <p className="timing-confirm-warning">
                      ⚡ {rich(t('<b>Tip:</b> Use this when words have correct order but timing is uneven.'), { b: (c) => <strong>{c}</strong> })}
                    </p>
                  </div>
                  <div className="timing-confirm-actions">
                    <button className="timing-confirm-btn cancel" onClick={handleCloseModal}>
                      {t('Cancel')}
                    </button>
                    <button className="timing-confirm-btn confirm" onClick={handleProportionalApply}>
                      {t('Adjust Proportionally')}
                    </button>
                  </div>
                </>
              )}

              {/* Reassign Mode Content */}
              {timingAdjustMode === 'reassign' && (
                <>
                  <div className="timing-reassign-container">
                    <div className="timing-reassign-header">
                      <div className="timing-col-header">{t('Audio Segments')}</div>
                      <div className="timing-col-header">{t('Assigned Words')}</div>
                    </div>
                    
                    <div className="timing-reassign-content">
                      {audioSegments.map((segment, index) => {
                        const wordsInSegment = getWordsForSegment(segment.id);
                        const isCurrentlyPlaying = playingSegmentId === segment.id;
                        
                        return (
                          <React.Fragment key={segment.id}>
                            <div className="timing-reassign-row">
                              {/* Audio Segment */}
                              <div className="timing-audio-segment">
                                <button 
                                  className={`timing-audio-play-btn ${isCurrentlyPlaying ? 'playing' : ''}`}
                                  onClick={() => playAudioSegment(segment)}
                                >
                                  {isCurrentlyPlaying ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <rect x="6" y="4" width="4" height="16" />
                                      <rect x="14" y="4" width="4" height="16" />
                                    </svg>
                                  ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  )}
                                </button>
                                <div className="timing-audio-timing" dir="ltr">
                                  <span className="timing-value">{formatTime(segment.startTime)}</span>
                                  <span className="timing-separator">→</span>
                                  <span className="timing-value">{formatTime(segment.endTime)}</span>
                                </div>
                              </div>

                              {/* Connector */}
                              <div className="timing-connector">
                                <div className="timing-connector-line"></div>
                              </div>

                              {/* Words - Drop Target */}
                              <div 
                                className={`timing-words-segment ${wordsInSegment.length === 0 ? 'empty' : ''} ${dragOverSegmentId === segment.id ? 'drag-over' : ''} ${draggedWordId ? 'drop-target' : ''}`}
                                onDragOver={(e) => handleDragOver(e, segment.id)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, segment.id)}
                              >
                                {wordsInSegment.length === 0 ? (
                                  <span className="timing-empty-label">
                                    {draggedWordId ? (dragOverSegmentId === segment.id ? `✓ ${t('Drop here')}` : t('Drop here')) : t('No word')}
                                  </span>
                                ) : (
                                  wordsInSegment.map(word => (
                                    <span 
                                      key={word.id}
                                      className={`timing-reassign-word ${draggedWordId === word.id ? 'dragging' : ''}`}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, word.id)}
                                      onDrag={handleDrag}
                                      onDragEnd={handleDragEnd}
                                      onClick={(e) => !draggedWordId && handleWordPopupClick(e, word.id)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (!draggedWordId) handleWordPopupClick(e, word.id);
                                      }}
                                    >
                                      {getDisplayText(word)}
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>

                            {/* Merge Button */}
                            {index < audioSegments.length - 1 && (
                              <div className="timing-merge-row">
                                <button 
                                  className="timing-merge-btn tooltip-trigger"
                                  onClick={() => mergeSegments(index)}
                                  data-tooltip={t('Merge with next segment')}
                                  aria-label={t('Merge with next segment')}
                                >
                                  {t('Merge the audios into one')}
              </button>
            </div>
                            )}
                          </React.Fragment>
                        );
                      })}
          </div>
        </div>

                  <div className="timing-help-text">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    {t('Drag words to reassign, or click for up/down options.')}
                  </div>

                  <div className="timing-confirm-actions">
                    <button className="timing-confirm-btn cancel" onClick={handleCloseModal}>
                      {t('Cancel')}
                    </button>
                    <button 
                      className="timing-confirm-btn confirm" 
                      onClick={handleReassignApply}
                      disabled={!hasReassignChanges}
                    >
                      {t('Apply Mapping')}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Word Movement Popup */}
            {selectedWordForPopup && (
              <div 
                className="timing-word-popup"
                style={{ left: popupPosition.x, top: popupPosition.y }}
                onClick={e => e.stopPropagation()}
              >
                {(() => {
                  const currentSegmentId = wordAssignments[selectedWordForPopup];
                  const currentSegmentIndex = audioSegments.findIndex(s => s.id === currentSegmentId);
                  const canMoveUp = currentSegmentIndex > 0;
                  const canMoveDown = currentSegmentIndex < audioSegments.length - 1;
                  
                  return (
                    <>
                      <button 
                        className={`timing-popup-btn ${!canMoveUp ? 'disabled' : ''}`}
                        onClick={() => canMoveUp && moveWordUp(selectedWordForPopup)}
                        disabled={!canMoveUp}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="18 15 12 9 6 15" />
                        </svg>
                        {t('Move Up')}
                      </button>
                      <button 
                        className={`timing-popup-btn ${!canMoveDown ? 'disabled' : ''}`}
                        onClick={() => canMoveDown && moveWordDown(selectedWordForPopup)}
                        disabled={!canMoveDown}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                        {t('Move Down')}
                      </button>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default CaptionEditor;
